import { useMemo, useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Funnel, FunnelSimple, MagnifyingGlass, X, CaretRight, CaretDown, SortAscending, SortDescending } from '@phosphor-icons/react';

/**
 * Excel-like column filter.
 * Props:
 *   column: { key, label, type }  // type: text|status|followup|classification|money|date|datetime
 *   values: array of raw values from the current dataset (for building option lists)
 *   filter: filter state (see below)
 *   onChange(newFilter): applied instantly
 *   sort: 'asc' | 'desc' | null  (current)
 *   onSort('asc'|'desc'|null)
 *   labelFor?: (rawValue) => string   // human label (statuses / classifications)
 *
 * Filter state shape:
 *   { type: 'multi',   selected: string[] }
 *   { type: 'date',    selected: string[] of 'YYYY-MM-DD' }
 *   { type: 'number',  op: 'eq'|'neq'|'gt'|'gte'|'lt'|'lte'|'between'|'empty'|'notempty', v1?: number, v2?: number }
 */

const NUM_OPS = [
  { code: 'eq',       label: 'يساوي' },
  { code: 'neq',      label: 'لا يساوي' },
  { code: 'gt',       label: 'أكبر من' },
  { code: 'gte',      label: 'أكبر من أو يساوي' },
  { code: 'lt',       label: 'أقل من' },
  { code: 'lte',      label: 'أقل من أو يساوي' },
  { code: 'between',  label: 'بين قيمتين' },
  { code: 'empty',    label: 'فارغ' },
  { code: 'notempty', label: 'غير فارغ' },
];

// Determine which filter shape a column uses
export function filterModeFor(type) {
  if (type === 'money') return 'number';
  if (type === 'date' || type === 'datetime') return 'date';
  return 'multi';
}

// Apply a single filter to a row value. Returns true if row passes.
export function passesFilter(rawValue, filter) {
  if (!filter) return true;
  if (filter.type === 'multi') {
    if (!filter.selected || filter.selected.length === 0) return true;
    const v = rawValue == null ? '__empty__' : String(rawValue);
    return filter.selected.includes(v);
  }
  if (filter.type === 'date') {
    if (!filter.selected || filter.selected.length === 0) return true;
    if (rawValue == null || rawValue === '') return filter.selected.includes('__empty__');
    const iso = String(rawValue).slice(0, 10); // YYYY-MM-DD
    return filter.selected.includes(iso);
  }
  if (filter.type === 'number') {
    const n = Number(rawValue);
    const isEmpty = rawValue == null || rawValue === '' || isNaN(n);
    switch (filter.op) {
      case 'empty':    return isEmpty;
      case 'notempty': return !isEmpty;
      case 'eq':       return !isEmpty && n === Number(filter.v1);
      case 'neq':      return !isEmpty && n !== Number(filter.v1);
      case 'gt':       return !isEmpty && n >  Number(filter.v1);
      case 'gte':      return !isEmpty && n >= Number(filter.v1);
      case 'lt':       return !isEmpty && n <  Number(filter.v1);
      case 'lte':      return !isEmpty && n <= Number(filter.v1);
      case 'between':  return !isEmpty && n >= Number(filter.v1) && n <= Number(filter.v2);
      default: return true;
    }
  }
  return true;
}

// Compact "is-active" test used to color the funnel icon
export function isFilterActive(filter) {
  if (!filter) return false;
  if (filter.type === 'multi') return (filter.selected || []).length > 0;
  if (filter.type === 'date')  return (filter.selected || []).length > 0;
  if (filter.type === 'number') return !!filter.op;
  return false;
}

// ----- Multi-select body (text/status/branch/employee/classification/followup/contract_type) -----
function MultiBody({ values, labelFor, filter, onChange }) {
  const [q, setQ] = useState('');
  const options = useMemo(() => {
    const set = new Map();
    for (const v of values) {
      const key = v == null || v === '' ? '__empty__' : String(v);
      const lbl = key === '__empty__' ? '(فارغ)' : (labelFor ? (labelFor(v) || key) : key);
      if (!set.has(key)) set.set(key, lbl);
    }
    const arr = Array.from(set.entries()).map(([k, l]) => ({ code: k, label: l }));
    arr.sort((a, b) => a.code === '__empty__' ? 1 : b.code === '__empty__' ? -1 : a.label.localeCompare(b.label, 'ar'));
    return arr;
  }, [values, labelFor]);

  const ql = q.trim().toLowerCase();
  const visible = ql ? options.filter(o => o.label.toLowerCase().includes(ql) || o.code.toLowerCase().includes(ql)) : options;
  const selected = new Set(filter?.selected || []);
  const allChecked = visible.length > 0 && visible.every(o => selected.has(o.code));
  const someChecked = visible.some(o => selected.has(o.code));

  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code); else next.add(code);
    onChange({ type: 'multi', selected: Array.from(next) });
  };
  const toggleAll = () => {
    if (allChecked) {
      const next = new Set(selected);
      visible.forEach(o => next.delete(o.code));
      onChange({ type: 'multi', selected: Array.from(next) });
    } else {
      const next = new Set(selected);
      visible.forEach(o => next.add(o.code));
      onChange({ type: 'multi', selected: Array.from(next) });
    }
  };

  return (
    <div className="space-y-2" data-testid="filter-multi-body">
      <div className="relative">
        <MagnifyingGlass className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pr-8 text-sm" placeholder="ابحث..." data-testid="filter-search" />
      </div>
      <label className="flex items-center gap-2 py-1 border-b border-slate-100 cursor-pointer">
        <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="filter-select-all" />
        <span className="text-sm font-semibold text-slate-900">تحديد الكل {ql && `(${visible.length})`}</span>
      </label>
      <div className="max-h-60 overflow-y-auto space-y-0.5">
        {visible.length === 0 ? (
          <div className="text-xs text-slate-400 text-center py-3">لا توجد قيم</div>
        ) : visible.map(o => (
          <label key={o.code} className="flex items-center gap-2 py-1 hover:bg-slate-50 rounded px-1 cursor-pointer">
            <Checkbox
              checked={selected.has(o.code)}
              onCheckedChange={() => toggle(o.code)}
              data-testid={`filter-opt-${o.code}`}
            />
            <span className="text-sm text-slate-700 truncate">{o.label}</span>
          </label>
        ))}
      </div>
      {someChecked && (
        <div className="text-xs text-slate-500 pt-1 border-t border-slate-100">
          {selected.size} قيمة محددة
        </div>
      )}
    </div>
  );
}

// ----- Hierarchical Date body: Year → Month → Day -----
function DateBody({ values, filter, onChange }) {
  const [q, setQ] = useState('');
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Build tree: year -> month -> [day]
  const tree = useMemo(() => {
    const t = new Map();
    const hasEmpty = values.some(v => v == null || v === '');
    for (const v of values) {
      if (v == null || v === '') continue;
      const iso = String(v).slice(0, 10);
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) continue;
      const [_, y, mo, d] = m;
      if (!t.has(y)) t.set(y, new Map());
      const yMap = t.get(y);
      if (!yMap.has(mo)) yMap.set(mo, new Set());
      yMap.get(mo).add(d);
    }
    return { tree: t, hasEmpty };
  }, [values]);

  const selected = new Set(filter?.selected || []);

  // Utilities
  const daysOfMonth = (y, mo) => Array.from(tree.tree.get(y)?.get(mo) || []).sort();
  const daysOfYear = (y) => {
    const out = [];
    const yMap = tree.tree.get(y) || new Map();
    yMap.forEach((days, mo) => days.forEach(d => out.push(`${y}-${mo}-${d}`)));
    return out;
  };
  const isDaySelected = (y, mo, d) => selected.has(`${y}-${mo}-${d}`);
  const monthCheckState = (y, mo) => {
    const days = daysOfMonth(y, mo);
    if (days.length === 0) return 'none';
    const sel = days.filter(d => selected.has(`${y}-${mo}-${d}`)).length;
    if (sel === 0) return 'none';
    if (sel === days.length) return 'all';
    return 'some';
  };
  const yearCheckState = (y) => {
    const all = daysOfYear(y);
    if (all.length === 0) return 'none';
    const sel = all.filter(iso => selected.has(iso)).length;
    if (sel === 0) return 'none';
    if (sel === all.length) return 'all';
    return 'some';
  };
  const allDates = useMemo(() => {
    const out = [];
    tree.tree.forEach((yMap, y) => yMap.forEach((days, mo) => days.forEach(d => out.push(`${y}-${mo}-${d}`))));
    if (tree.hasEmpty) out.push('__empty__');
    return out;
  }, [tree]);
  const allState = (() => {
    if (allDates.length === 0) return 'none';
    const sel = allDates.filter(x => selected.has(x)).length;
    if (sel === 0) return 'none';
    if (sel === allDates.length) return 'all';
    return 'some';
  })();

  const commit = (setLike) => onChange({ type: 'date', selected: Array.from(setLike) });

  const toggleDay = (iso) => {
    const next = new Set(selected);
    if (next.has(iso)) next.delete(iso); else next.add(iso);
    commit(next);
  };
  const toggleMonth = (y, mo) => {
    const days = daysOfMonth(y, mo);
    const state = monthCheckState(y, mo);
    const next = new Set(selected);
    if (state === 'all') days.forEach(d => next.delete(`${y}-${mo}-${d}`));
    else days.forEach(d => next.add(`${y}-${mo}-${d}`));
    commit(next);
  };
  const toggleYear = (y) => {
    const iso = daysOfYear(y);
    const state = yearCheckState(y);
    const next = new Set(selected);
    if (state === 'all') iso.forEach(x => next.delete(x));
    else iso.forEach(x => next.add(x));
    commit(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    if (allState === 'all') allDates.forEach(x => next.delete(x));
    else allDates.forEach(x => next.add(x));
    commit(next);
  };

  const ql = q.trim();
  const years = Array.from(tree.tree.keys()).sort((a, b) => b.localeCompare(a));
  const visibleYears = ql ? years.filter(y => y.includes(ql)) : years;
  const MONTH_NAMES_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

  return (
    <div className="space-y-2" data-testid="filter-date-body">
      <div className="relative">
        <MagnifyingGlass className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pr-8 text-sm" placeholder="ابحث بالسنة..." data-testid="filter-date-search" />
      </div>
      <label className="flex items-center gap-2 py-1 border-b border-slate-100 cursor-pointer">
        <Checkbox checked={allState === 'all'} onCheckedChange={toggleAll} data-testid="filter-date-select-all" />
        <span className="text-sm font-semibold text-slate-900">تحديد الكل</span>
      </label>
      <div className="max-h-72 overflow-y-auto space-y-0.5 text-sm">
        {visibleYears.length === 0 && <div className="text-xs text-slate-400 text-center py-3">لا توجد تواريخ</div>}
        {visibleYears.map(y => {
          const yState = yearCheckState(y);
          const isYearExpanded = expandedYears.has(y);
          const yMap = tree.tree.get(y) || new Map();
          const months = Array.from(yMap.keys()).sort();
          return (
            <div key={y}>
              <div className="flex items-center gap-1 hover:bg-slate-50 rounded px-1 py-0.5">
                <button onClick={() => setExpandedYears(prev => { const n = new Set(prev); n.has(y) ? n.delete(y) : n.add(y); return n; })} className="text-slate-500 w-4" data-testid={`filter-date-toggle-year-${y}`}>
                  {isYearExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                </button>
                <Checkbox
                  checked={yState === 'all' ? true : yState === 'some' ? 'indeterminate' : false}
                  onCheckedChange={() => toggleYear(y)}
                  data-testid={`filter-date-year-${y}`}
                />
                <span className="font-semibold text-slate-900">{y}</span>
              </div>
              {isYearExpanded && months.map(mo => {
                const mState = monthCheckState(y, mo);
                const monthKey = `${y}-${mo}`;
                const isMoExpanded = expandedMonths.has(monthKey);
                const days = daysOfMonth(y, mo);
                return (
                  <div key={monthKey} className="mr-4">
                    <div className="flex items-center gap-1 hover:bg-slate-50 rounded px-1 py-0.5">
                      <button onClick={() => setExpandedMonths(prev => { const n = new Set(prev); n.has(monthKey) ? n.delete(monthKey) : n.add(monthKey); return n; })} className="text-slate-500 w-4" data-testid={`filter-date-toggle-month-${monthKey}`}>
                        {isMoExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                      </button>
                      <Checkbox
                        checked={mState === 'all' ? true : mState === 'some' ? 'indeterminate' : false}
                        onCheckedChange={() => toggleMonth(y, mo)}
                        data-testid={`filter-date-month-${monthKey}`}
                      />
                      <span className="text-slate-700">{MONTH_NAMES_AR[parseInt(mo, 10) - 1]}</span>
                    </div>
                    {isMoExpanded && (
                      <div className="mr-4 grid grid-cols-1 gap-0.5">
                        {days.map(d => {
                          const iso = `${y}-${mo}-${d}`;
                          return (
                            <label key={iso} className="flex items-center gap-1 hover:bg-slate-50 rounded px-1 py-0.5 cursor-pointer">
                              <Checkbox checked={isDaySelected(y, mo, d)} onCheckedChange={() => toggleDay(iso)} data-testid={`filter-date-day-${iso}`} />
                              <span className="text-xs text-slate-600 mono">{iso}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        {tree.hasEmpty && (
          <label className="flex items-center gap-2 py-1 hover:bg-slate-50 rounded px-1 cursor-pointer border-t border-slate-100 mt-1 pt-2">
            <Checkbox checked={selected.has('__empty__')} onCheckedChange={() => toggleDay('__empty__')} />
            <span className="text-sm text-slate-500 italic">(فارغ)</span>
          </label>
        )}
      </div>
    </div>
  );
}

// ----- Number body -----
function NumberBody({ filter, onChange }) {
  const [op, setOp] = useState(filter?.op || 'gte');
  const [v1, setV1] = useState(filter?.v1 ?? '');
  const [v2, setV2] = useState(filter?.v2 ?? '');
  const needsV1 = !['empty', 'notempty'].includes(op);
  const needsV2 = op === 'between';

  // Sync local state when parent clears the filter (e.g. "مسح" button)
  useEffect(() => {
    if (filter == null) {
      setOp('gte');
      setV1('');
      setV2('');
    } else if (filter.type === 'number') {
      setOp(filter.op || 'gte');
      setV1(filter.v1 ?? '');
      setV2(filter.v2 ?? '');
    }
  }, [filter]);

  useEffect(() => {
    // instant apply
    if (!op) return;
    if (['empty', 'notempty'].includes(op)) {
      onChange({ type: 'number', op });
    } else if (op === 'between') {
      if (v1 !== '' && v2 !== '') onChange({ type: 'number', op, v1: Number(v1), v2: Number(v2) });
    } else {
      if (v1 !== '') onChange({ type: 'number', op, v1: Number(v1) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [op, v1, v2]);

  return (
    <div className="space-y-2" data-testid="filter-number-body">
      <div>
        <label className="text-xs text-slate-500 mb-1 block">العملية</label>
        <Select value={op} onValueChange={setOp}>
          <SelectTrigger className="h-8" data-testid="filter-num-op"><SelectValue /></SelectTrigger>
          <SelectContent>
            {NUM_OPS.map(o => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {needsV1 && (
        <div>
          <label className="text-xs text-slate-500 mb-1 block">القيمة</label>
          <Input type="number" value={v1} onChange={(e) => setV1(e.target.value)} className="h-8 text-sm" placeholder="مثال: 1000" data-testid="filter-num-v1" />
        </div>
      )}
      {needsV2 && (
        <div>
          <label className="text-xs text-slate-500 mb-1 block">إلى</label>
          <Input type="number" value={v2} onChange={(e) => setV2(e.target.value)} className="h-8 text-sm" placeholder="مثال: 5000" data-testid="filter-num-v2" />
        </div>
      )}
    </div>
  );
}

// ----- Main component -----
export default function ExcelFilter({ column, values, filter, onChange, sort, onSort, labelFor, onOpen }) {
  const mode = filterModeFor(column.type);
  const active = isFilterActive(filter);

  return (
    <Popover onOpenChange={(open) => { if (open && typeof onOpen === 'function') onOpen(); }}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${active ? 'text-[#B4935A] bg-[#B4935A]/10' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`col-filter-${column.key}`}
          title="تصفية"
        >
          {active ? <Funnel size={14} weight="fill" /> : <FunnelSimple size={14} />}
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-72 p-3" dir="rtl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
          <div className="flex items-center gap-1">
            <button
              onClick={() => onSort(sort === 'asc' ? null : 'asc')}
              className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${sort === 'asc' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="filter-sort-asc"
            ><SortAscending size={12} /> تصاعدي</button>
            <button
              onClick={() => onSort(sort === 'desc' ? null : 'desc')}
              className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${sort === 'desc' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="filter-sort-desc"
            ><SortDescending size={12} /> تنازلي</button>
          </div>
          <button
            onClick={() => { onChange(null); onSort(null); }}
            className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1"
            data-testid="filter-clear"
            disabled={!active && !sort}
          ><X size={10} /> مسح</button>
        </div>
        {mode === 'multi'  && <MultiBody  values={values} labelFor={labelFor} filter={filter} onChange={onChange} />}
        {mode === 'date'   && <DateBody   values={values} filter={filter} onChange={onChange} />}
        {mode === 'number' && <NumberBody filter={filter} onChange={onChange} />}
      </PopoverContent>
    </Popover>
  );
}
